import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';

export default function SectionEditor({ sectionKey, title, fields, defaults = {} }) {
  const api = useAdminApi();
  const [data, setData] = useState(defaults);
  const [original, setOriginal] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const dirty = useMemo(
    () => JSON.stringify(data) !== JSON.stringify(original),
    [data, original]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/sections/${encodeURIComponent(sectionKey)}`);
        if (!alive) return;
        const merged = { ...defaults, ...(res || {}) };
        setData(merged);
        setOriginal(merged);
      } catch (e) {
        setData(defaults);
        setOriginal(defaults);
        // optional: don't scare on first-load if not found
        // setError(e?.message || 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  const onChange = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const onSave = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/sections/${encodeURIComponent(sectionKey)}`, data);
      setOriginal(data);
      setEditing(false);
      setLastSavedAt(new Date());
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => { setData(original); setEditing(false); };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border brand-spinner" role="status" />
        <div className="small mt-2 text-muted">Lädt…</div>
      </div>
    );
  }

  return (
    <div>
      <div className="editor-toolbar">
        <div className="d-flex align-items-center gap-2">
          <h4 className="editor-title m-0">{title}</h4>
          {editing && dirty && <span className="badge-soft">• Ungespeicherte Änderungen</span>}
          {!editing && lastSavedAt && (
            <span className="badge-soft" title={lastSavedAt.toISOString()}>
              Gespeichert {new Intl.DateTimeFormat('de-DE', { hour:'2-digit', minute:'2-digit' }).format(lastSavedAt)}
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {!editing ? (
            <button className="btn btn-sm btn-edit" onClick={() => setEditing(true)}>
              Bearbeiten
            </button>
          ) : (
            <>
              <button
                className="btn btn-sm btn-save"
                onClick={onSave}
                disabled={!dirty || saving}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              <button className="btn btn-sm btn-cancel" onClick={onCancel} disabled={saving}>
                Abbrechen
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger m-3 py-2" role="alert">{error}</div>}

      <div className="p-3">
        <form>
          {fields.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              value={data[f.key] ?? ''}
              readOnly={!editing}
              onChange={onChange}
            />
          ))}
        </form>
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange, readOnly }) {
  const id = `field_${field.key}`;
  const commonProps = {
    id,
    className: 'form-control',
    value: value ?? '',
    onChange: (e) => onChange(field.key, e.target.value),
    readOnly,
    placeholder: field.placeholder,
  };

  return (
    <div className="mb-3">
      <label htmlFor={id} className="form-label">{field.label}</label>

      {field.type === 'textarea' && (
        <textarea {...commonProps} rows={field.rows || 5} />
      )}

      {field.type === 'date' && (
        <input type="date" {...commonProps} />
      )}

      {field.type === 'number' && (
        <input type="number" {...commonProps} />
      )}

      {field.type === 'datetime-local' && (
        <input type="datetime-local" {...commonProps} placeholder={field.placeholder} />
      )}

      {field.type === 'select' && (
        <select
          {...commonProps}
          className="form-select"
          disabled={readOnly}
          onChange={(e) => onChange(field.key, field.numeric ? Number(e.target.value) : e.target.value)}
        >
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {(!field.type || ['text', 'email', 'url'].includes(field.type)) && (
        <input type={field.type || 'text'} {...commonProps} />
      )}

      {field.help && <div className="form-text">{field.help}</div>}
    </div>
  );
}
